import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '../api/client'
import type { ActivitySummary, StatsSummary } from '../api/types'
import { exerciseTypeLabel } from '../api/types'
import { UploadIcon } from '../components/Icons'
import { dayAndTime, distanceKm, duration, integer, pace } from '../format'
import { useAuth } from '../auth'

const WINDOWS = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 365, label: 'Last year' },
  { days: 3650, label: 'All time' },
]

export function DashboardPage() {
  const { signOut } = useAuth()
  const [activities, setActivities] = useState<ActivitySummary[] | null>(null)
  const [stats, setStats] = useState<StatsSummary | null>(null)
  const [days, setDays] = useState(30)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  /** Progress across a batch, so dropping twelve files says "3 of 12" rather than a list. */
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [result, setResult] = useState<{ added: number; failed: string[] } | null>(null)
  const [dragging, setDragging] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  const handleFailure = useCallback(
    (failure: unknown) => {
      if (failure instanceof ApiError && failure.isUnauthorised) {
        signOut()
        return
      }
      setError(failure instanceof Error ? failure.message : 'Something went wrong')
    },
    [signOut],
  )

  const load = useCallback(async () => {
    setError(null)
    try {
      const [page, summary] = await Promise.all([api.activities(0, 50), api.stats(days)])
      setActivities(page.content)
      setStats(summary)
    } catch (failure) {
      handleFailure(failure)
    }
  }, [days, handleFailure])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * Uploads a batch, one file at a time.
   *
   * Each file is tried on its own rather than the whole batch sharing a try: a single malformed
   * file used to throw out of the loop, so everything after it was silently skipped. Now a bad
   * file is counted and the rest still go up. The one exception is an expired session, where
   * carrying on would just fail every remaining file for the same reason.
   */
  // Dropping a file anywhere outside the zone makes the browser open it, navigating away from
  // the dashboard and losing the page. Swallowing it keeps a near miss harmless.
  useEffect(() => {
    const swallow = (event: DragEvent) => event.preventDefault()
    window.addEventListener('dragover', swallow)
    window.addEventListener('drop', swallow)
    return () => {
      window.removeEventListener('dragover', swallow)
      window.removeEventListener('drop', swallow)
    }
  }, [])

  async function uploadFiles(dropped: File[]) {
    const files = dropped.filter(isTcx)
    if (files.length === 0) {
      setResult(null)
      setError(
        dropped.length === 0
          ? 'No files were dropped.'
          : 'Those are not .tcx files. Export a .tcx from your watch or another app.',
      )
      return
    }

    setUploading(true)
    setError(null)
    setResult(null)
    setProgress({ done: 0, total: files.length })

    const failed: string[] = []
    let added = 0
    for (const [index, file] of files.entries()) {
      setProgress({ done: index, total: files.length })
      try {
        await api.uploadTcx(await file.text())
        added += 1
      } catch (failure) {
        failed.push(file.name)
        if (failure instanceof ApiError && failure.isUnauthorised) {
          handleFailure(failure)
          break
        }
      }
    }

    setProgress({ done: files.length, total: files.length })
    setResult({ added, failed })
    await load()
    setUploading(false)
    // Cleared so that picking the same file again still fires a change event.
    if (fileInput.current) fileInput.current.value = ''
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Your activities</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <label htmlFor="window" style={{ margin: 0 }}>
            Window
          </label>
          <select
            id="window"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            {WINDOWS.map((w) => (
              <option key={w.days} value={w.days}>
                {w.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="tiles">
        <Tile label="Activities" value={stats ? String(stats.runs) : '—'} />
        <Tile label="Distance" value={stats ? distanceKm(stats.distanceMeters) : '—'} unit="km" />
        <Tile label="Moving time" value={stats ? duration(stats.movingSeconds) : '—'} />
        <Tile
          label="Average pace"
          value={stats ? pace(stats.avgPaceSecondsPerKm) : '—'}
          unit="/km"
        />
        <Tile
          label="Average HR"
          value={stats?.avgHeartRateBpm ? integer(stats.avgHeartRateBpm) : '—'}
          unit="bpm"
        />
        <Tile label="Calories" value={stats ? integer(stats.calories) : '—'} unit="kcal" />
      </div>

      <h2>Activities</h2>

      {error && <div className="error">{error}</div>}

      {activities === null ? (
        <div className="spinner">Loading your activities…</div>
      ) : activities.length === 0 ? (
        <div className="empty">
          <p style={{ marginTop: 0 }}>
            <strong>No activities uploaded yet.</strong>
          </p>
          <p>
            Record one in the My Tracker app and tap Finish — it uploads as soon as the phone is
            back on a network. You can also add a .tcx file from a watch below.
          </p>
        </div>
      ) : (
        <div className="run-list">
          {activities.map((run) => (
            <Link key={run.id} to={`/runs/${run.id}`} className="run-row">
              <div className="activity-when">
                <div className="run-when">{dayAndTime(run.startTime)}</div>
                <div className="muted" style={{ fontSize: 13 }}>
                  {[run.creatorName, run.lapCount > 1 ? `${run.lapCount} laps` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
              {/* Its own column rather than a word in the subtitle, so a list of mixed
                  activities can be scanned down. The exercise type, not run.sport: TCX has
                  no way to say "walk" and would call it "Other". */}
              <span className="pill activity-type">{exerciseTypeLabel(run.exerciseType)}</span>
              <div className="run-stats">
                <span>
                  <strong>{distanceKm(run.distanceMeters)}</strong> km
                </span>
                <span>
                  <strong>{duration(run.totalTimeSeconds)}</strong>
                </span>
                <span>
                  <strong>{pace(run.avgPaceSecondsPerKm)}</strong> /km
                </span>
                {run.avgHeartRateBpm !== undefined && (
                  <span>
                    <strong>{run.avgHeartRateBpm}</strong> bpm
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      <h2>Add a file</h2>
      <div
        className={`card dropzone${dragging ? ' dropzone-active' : ''}`}
        onDragOver={(e) => {
          e.preventDefault()
          if (!uploading) setDragging(true)
        }}
        onDragLeave={(e) => {
          // Moving across a child fires dragleave on the zone; only react on a real exit.
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          if (!uploading) void uploadFiles(Array.from(e.dataTransfer.files))
        }}
      >
        <p className="muted" style={{ marginTop: 0 }}>
          Drop .tcx files here, or choose them below. Activities recorded in My Tracker arrive on
          their own; re-uploading one you already have changes nothing.
        </p>
        {/* The native control is hidden and this label stands in for it; see .file-field. */}
        <div className="file-field">
          <input
            id="tcxUpload"
            ref={fileInput}
            type="file"
            accept=".tcx,application/xml,text/xml"
            multiple
            disabled={uploading}
            onChange={(e) => {
              const files = e.target.files
              if (files?.length) void uploadFiles(Array.from(files))
            }}
          />
          <label className="file-button" htmlFor="tcxUpload">
            <UploadIcon />
            Choose .tcx files
          </label>
          <span className="file-status">
            {uploading
              ? progress.total > 1
                ? `Uploading ${progress.done + 1} of ${progress.total}…`
                : 'Uploading…'
              : result
                ? [
                    result.added > 0 ? `Added ${result.added}` : null,
                    result.failed.length > 0 ? `${result.failed.length} failed` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : 'No file chosen'}
          </span>
        </div>
        {result && result.failed.length > 0 && (
          <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
            Not accepted: {result.failed.join(', ')}. The server reads Garmin TCX v2; a file it
            cannot parse is skipped and the rest still upload.
          </p>
        )}
      </div>
    </div>
  )
}

/** Accepts by extension, since browsers report .tcx with no reliable MIME type of its own. */
function isTcx(file: File): boolean {
  return file.name.toLowerCase().endsWith('.tcx')
}

function Tile({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="tile">
      <div className="tile-label">{label}</div>
      <div className="tile-value">
        {value}
        {unit && <span className="tile-unit">{unit}</span>}
      </div>
    </div>
  )
}
